// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { CommissionItem } from './CommissionItem';
import { RevenueCommissionType } from '@shared/utils/Enums/Constants';
import { CommissionOperationType, CommissionTypes } from '@shared/utils/Commission/CommissionEnums';
import { CommissionItemParams } from './CommissionItemParams';
import { commisionSplitter } from '@shared/utils/Commission/CommisionSplitter';
import { KEY_ORDER_PER_LOT_WITH_FIXED_PART } from '@shared/utils/Trading/FeeTextConstants';
export class CommissionPlan {
    constructor(dCache) {
        this.Id = null;
        this.Name = null;
        this.Description = null;
        this.CommisionForTransfer = null;
        this.CounterAccountID = null;
        this.TransferCommisionType = RevenueCommissionType.None;
        this.WithdrawalCommisionType = RevenueCommissionType.None;
        this.TransferPercentage = null;
        this.WithdrawalPercentage = null;
        this.TransferFixedFee = null;
        this.WithdrawalFixedFee = null;
        this.TransferMinFee = null;
        this.WithdrawalMinFee = null;
        this.TransferMaxFee = null;
        this.WithdrawalMaxFee = null;
        this.TransferCurrencyId = null;
        this.WithdrawalCurrencyId = null;
        this.TransferAccountId = null;
        this.WithdrawalAccountId = null;
        this._itemsByInstrumentId = {};
        this._itemsByType = {};
        this._defaultCommissionItem = null;
        this._itemsByInstrumentIdHasValue = false;
        this._itemsByTypeHasValue = false;
        this.DCache = dCache;
    }
    Update(msg) {
        this.Id = msg.Id;
        this.Name = msg.Name;
        this.Description = msg.Description;
        this.CommisionForTransfer = msg.CommisionForTransfer;
        this.CounterAccountID = msg.CounterAccountID;
        this.TransferCommisionType = msg.TransferCommisionType;
        this.WithdrawalCommisionType = msg.WithdrawalCommisionType;
        this.TransferPercentage = msg.TransferPercentage;
        this.WithdrawalPercentage = msg.WithdrawalPercentage;
        this.TransferFixedFee = msg.TransferFixedFee;
        this.WithdrawalFixedFee = msg.WithdrawalFixedFee;
        this.TransferMinFee = msg.TransferMinFee;
        this.WithdrawalMinFee = msg.WithdrawalMinFee;
        this.TransferMaxFee = msg.TransferMaxFee;
        this.WithdrawalMaxFee = msg.WithdrawalMaxFee;
        this.TransferCurrencyId = msg.TransferCurrencyId;
        this.WithdrawalCurrencyId = msg.WithdrawalCurrencyId;
        this.TransferAccountId = msg.TransferAccountId;
        this.WithdrawalAccountId = msg.WithdrawalAccountId;
        const items = msg.CommissionItems;
        for (let i = 0; i < items.length; i++) {
            this.AddItem(items[i]);
        }
    }
    AddItem(data) {
        const item = new CommissionItem(data);
        const insID = data.InstrumentId;
        const insTypeID = data.InstrumentTypeId;
        if (insID) {
            if (!this._itemsByInstrumentId[insID]) {
                this._itemsByInstrumentId[insID] = item;
            }
            else {
                this._itemsByInstrumentId[insID].Add(data);
            }
            this._itemsByInstrumentIdHasValue = true;
        }
        else if (insTypeID !== -1) {
            if (!this._itemsByType[insTypeID]) {
                this._itemsByType[insTypeID] = item;
            }
            else {
                this._itemsByType[insTypeID].Add(data);
            }
            this._itemsByTypeHasValue = true;
        }
        else {
            if (!this._defaultCommissionItem) {
                this._defaultCommissionItem = item;
            }
            else {
                this._defaultCommissionItem.Add(data);
            }
        }
    }
    GetCommissionItem(instrument) {
        // получение CommissionItem по инструменту
        if (isNullOrUndefined(instrument)) {
            return null;
        }
        let item = this._itemsByInstrumentId[instrument.Id];
        if (item) {
            return item;
        }
        let typeID = instrument.TypeId;
        while (typeID !== -1) {
            item = this._itemsByType[typeID];
            if (item) {
                return item;
            }
            const insType = this.DCache.getInstrumentTypeById(typeID);
            typeID = insType != null ? insType.SuperTypeId : -1; // поищем определяющий CommissionItem в инстр.группах-предках, пока не упремся в корневую
        }
        return this._defaultCommissionItem;
    }
    AddFeeItems(itemsForTable, ins) {
        const item = this.GetCommissionItem(ins);
        if (item) {
            item.AddFeeItems(itemsForTable, this.Id, ins);
        }
    }
    GetAllowedCommissionTypes(ins) {
        const it = this.GetCommissionItem(ins);
        const res = {};
        if (!it) {
            return res;
        }
        for (let i = 0; i < it.Entry.length; i++) {
            const ent = it.Entry[i];
            const ci = ent.CommissionItems;
            for (let j = 0; j < ci.length; j++) {
                res[ci[j].Type] = true;
            }
        }
        return res;
    }
    static TryGetCommissionDataValue(feeCurrency, key, instrument, account) {
        var _a, _b, _c;
        const [typeKey, operKey, from /* to */ /* feeNameID */, , , planID] = key.split(commisionSplitter);
        const params = new CommissionItemParams();
        params.CommissionType = CommissionItem.GetCommissionTypeByKey(typeKey);
        params.OperationType = (_a = CommissionItem.OperationTypeSuffix[operKey]) !== null && _a !== void 0 ? _a : CommissionOperationType.ALL;
        params.Lots = parseFloat(from);
        if (params.CommissionType === CommissionTypes.OrderPerLotWithFixedPart) {
            const plan = account.CommissionPlansList.find((plan) => plan.Id === Number(planID));
            return (_c = (_b = plan === null || plan === void 0 ? void 0 : plan.GetCommissionItem(instrument)) === null || _b === void 0 ? void 0 : _b.GetCommissionDataValue(feeCurrency, key)) !== null && _c !== void 0 ? _c : null;
        }
        const commissionItems = account.CommissionPlansList.map((plan) => plan.GetCommissionItem(instrument)).filter((item) => item !== null);
        if (commissionItems.length === 0) {
            return null;
        }
        let totalValue = 0;
        for (const commissionItem of commissionItems) {
            const commissionValue = commissionItem.GetCommission(params);
            if (commissionValue) {
                totalValue += commissionValue;
            }
        }
        if (!totalValue) {
            return commissionItems[0].GetCommissionDataValue(feeCurrency, key);
        }
        return CommissionItem.FormatCommissionValue(totalValue, feeCurrency, params.CommissionType, key);
    }
    static GetFeeItems(ins, acc) {
        const { groups, rawItems } = this.buildGroupsWithPoints(acc, ins);
        const filtered = this.buildFilteredItems(groups);
        return [...filtered, ...rawItems];
    }
    static buildGroupsWithPoints(acc, ins) {
        const groups = new Map();
        const rawItems = [];
        acc.CommissionPlansList.forEach((plan) => {
            const tempItems = [];
            plan.AddFeeItems(tempItems, ins);
            tempItems.forEach((item) => {
                if (item.includes(KEY_ORDER_PER_LOT_WITH_FIXED_PART)) {
                    rawItems.push(item);
                    return;
                }
                const [typeKey, operKey, fromStr, toStr] = item.split(commisionSplitter);
                const key = `${typeKey}${commisionSplitter}${operKey}`;
                const from = parseFloat(fromStr);
                const to = parseFloat(toStr);
                if (!groups.has(key)) {
                    groups.set(key, { points: new Set(), entries: [] });
                }
                const group = groups.get(key);
                group.points.add(from);
                if (to !== -1)
                    group.points.add(to);
                group.entries.push({ from, to });
            });
        });
        return { groups, rawItems };
    }
    static buildFilteredItems(groups) {
        const filteredItems = [];
        groups.forEach(({ points, entries }, key) => {
            const sortedPoints = Array.from(points).sort((a, b) => a - b);
            sortedPoints.push(Infinity);
            for (let i = 0; i < sortedPoints.length - 1; i++) {
                const from = sortedPoints[i];
                const to = sortedPoints[i + 1];
                if (entries.some((entry) => from >= entry.from && to <= (entry.to === -1 ? Infinity : entry.to))) {
                    filteredItems.push(`${key}${commisionSplitter}${from}${commisionSplitter}${to === Infinity ? -1 : to}`);
                }
            }
        });
        return filteredItems;
    }
}
